/**
 * Flow Event Service — sends step-progression breadcrumbs to backend
 * for admin visibility into where patients get stuck. Non-blocking, never throws.
 *
 * Events are queued and flushed in batches (max one request every few seconds
 * instead of one request per event). On page hide the queue is flushed via
 * sendBeacon so the last breadcrumb isn't lost.
 */
import { getTenantSlug, whenTenantResolved } from '../context/TenantContext'

const API_URL = import.meta.env.VITE_API_URL || '/api'
const FLUSH_INTERVAL_MS = 4000
const MAX_BATCH = 50

export type FlowEventType = 'step_entered' | 'step_completed' | 'flow_started' | 'flow_completed' | 'error' | 'sepa_deferred'

interface QueuedEvent {
  patientNumber: string
  step: string
  eventType: FlowEventType
  metadata?: Record<string, unknown>
}

let queue: QueuedEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
// Dedupe: never send the same (patient, step, eventType) twice in one page session
const sentKeys = new Set<string>()
// Test mode disables all backend writes so test runs don't pollute the funnel
let disabled = false

/** Disable flow-event reporting for the rest of this page session (test mode). */
export function disableFlowEvents(): void {
  disabled = true
  queue = []
}

async function flush(useBeacon = false): Promise<void> {
  if (queue.length === 0) return
  const events = queue.slice(0, MAX_BATCH)
  queue = queue.slice(events.length)

  const body = JSON.stringify({ events })
  // A beacon (page closing) cannot wait; anything else waits for the org like api.ts
  if (!useBeacon) await whenTenantResolved()
  const slug = getTenantSlug()
  // Tenant goes in the URL too: sendBeacon can't set headers, and the backend
  // tenant middleware accepts ?org= as a fallback to X-Tenant-ID.
  const url = `${API_URL}/flow-events/batch${slug ? `?org=${encodeURIComponent(slug)}` : ''}`

  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
      return
    }
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (slug) headers['X-Tenant-ID'] = slug
    await fetch(url, { method: 'POST', headers, body, keepalive: true })
  } catch {
    // Silently fail — never interrupt patient flow for analytics
  }
}

function scheduleFlush(): void {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flush()
  }, FLUSH_INTERVAL_MS)
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true)
  })
}

export function logFlowEvent(
  patientNumber: string,
  step: string,
  eventType: FlowEventType = 'step_entered',
  metadata?: Record<string, unknown>
): void {
  if (disabled) return
  if (!patientNumber || !/^P\d+$/.test(patientNumber)) return

  const key = `${patientNumber}|${step}|${eventType}`
  if (sentKeys.has(key)) return
  sentKeys.add(key)

  queue.push({ patientNumber, step, eventType, metadata })
  scheduleFlush()
}
